import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";

const LINKS = [
  { label: "Destination", href: "#destination" },
  { label: "Architecture", href: "#architecture" },
  { label: "Amenities", href: "#amenities" },
  { label: "Residences", href: "#residences" },
  { label: "Location", href: "#location" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (e, href) => {
    e.preventDefault();
    setMenuOpen(false);
    setTimeout(() => {
      document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
    }, 60);
  };

  const light = !scrolled || menuOpen;

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 2.4, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed inset-x-0 top-0 z-50 transition-colors duration-500 ${
          menuOpen
            ? "bg-transparent"
            : scrolled
              ? "border-b border-[#1C1917]/10 bg-[#FAF9F6]"
              : "bg-transparent"
        }`}
        data-testid="main-nav"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-6 py-4 md:px-12">
          <a href="#top" onClick={(e) => go(e, "#top")} className="leading-none" data-testid="nav-logo">
            <span
              className={`block text-[10px] font-semibold tracking-[0.44em] transition-colors duration-500 ${
                light ? "text-[#F5F1E8]/80" : "text-[#57534E]"
              }`}
            >
              EMBASSY
            </span>
            <span
              className={`font-display mt-1 block text-lg tracking-[0.16em] transition-colors duration-500 ${
                light ? "text-[#F5F1E8]" : "text-[#064E3B]"
              }`}
            >
              SOUTH RESERVE
            </span>
          </a>
          <nav className="hidden items-center gap-8 lg:flex">
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={(e) => go(e, l.href)}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={`group relative text-[10px] font-medium uppercase tracking-[0.3em] transition-colors duration-500 ${
                  light ? "text-[#F5F1E8]/75 hover:text-[#F5F1E8]" : "text-[#57534E] hover:text-[#1C1917]"
                }`}
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-[#A98B5F] transition-[width] duration-500 group-hover:w-full" />
              </a>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <button
              onClick={(e) => go(e, "#enquire")}
              data-testid="nav-enquire-btn"
              className={`rounded-full px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.26em] transition-colors duration-500 ${
                light
                  ? "border border-[#F5F1E8]/50 text-[#F5F1E8] hover:bg-[#F5F1E8] hover:text-[#022C22]"
                  : "bg-[#064E3B] text-[#F5F1E8] hover:bg-[#022C22]"
              }`}
            >
              Enquire
            </button>
            <button
              onClick={() => setMenuOpen((v) => !v)}
              data-testid="nav-menu-btn"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              className={`flex h-10 w-10 items-center justify-center rounded-full border transition-colors duration-500 lg:hidden ${
                light
                  ? "border-[#F5F1E8]/50 text-[#F5F1E8]"
                  : "border-[#1C1917]/20 text-[#1C1917]"
              }`}
            >
              {menuOpen ? <X size={16} /> : <Menu size={16} />}
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 z-40 flex flex-col justify-between bg-[#022C22] px-8 pb-10 pt-28 lg:hidden"
            data-testid="mobile-menu"
          >
            <nav className="flex flex-col">
              {LINKS.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.07, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  className="font-display border-b border-[#F5F5F4]/10 py-5 text-3xl font-light tracking-wide text-[#F5F1E8]"
                >
                  {l.label}
                </motion.a>
              ))}
              <motion.a
                href="#enquire"
                onClick={(e) => go(e, "#enquire")}
                data-testid="mobile-nav-link-enquire"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="font-display border-b border-[#F5F5F4]/10 py-5 text-3xl font-light italic tracking-wide text-[#A98B5F]"
              >
                Enquire
              </motion.a>
            </nav>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 0.6 }}
              className="text-[11px] leading-relaxed text-[#F5F5F4]/50"
            >
              <p>+91 88800 50000</p>
              <p className="mt-1">sales@embassysouthreserve.com</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
